import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createBillingExport, decideBill, getBillProof, getBillingExport, issueBill,
  listBilling, recordCounterPayment, submitProof,
} from '../api/billingApi';
import {
  createBranch, createBranchExport, deleteBranch, getBranchExport, listBranches, updateBranch,
} from '../api/branchesApi';
import { getOperationsDashboard } from '../api/dashboardApi';
import {
  appealClaim, attachClaimDocument, createClaimsExport, decideClaim, fileClaim,
  getBenefitCatalog, getClaimAttachmentLink, getClaimsExport, listClaimAttachments,
  listClaims, updateBenefit,
} from '../api/claimsApi';
import {
  createBankHoliday, deleteBankHoliday, listBankHolidays, updateBankHoliday,
} from '../api/bankHolidaysApi';
import {
  createMember, createMemberExport, getMember, getMemberExport, getMyMember, listMembers,
  deleteMember, resetMemberPassword, revokeReferralToken, rotateReferralToken, transferUpline,
  updateMember, updateMyBeneficiaries,
} from '../api/membersApi';
import { getCommissionSummary, getMemberTree, getMyTree, listCommissions } from '../api/networkApi';
import {
  decidePayout, listPayouts, previewPayout, requestPayout, reschedulePayout,
} from '../api/payoutsApi';
import {
  approveOverrideRelease, getOverrideLedger, listOverrideReleases, requestOverrideRelease,
} from '../api/overridesApi';
import {
  createReportExport, getAcquisition, getBranchPerformance, getReportExport,
  getReportSummary, getRevenueTrend,
} from '../api/reportsApi';
import {
  assignTicket, createTicket, getSupportUnread, getTicketThread, listTickets,
  markTicketRead, replyToTicket, setTicketStatus,
} from '../api/supportApi';
import { passwordPolicyRequest } from '../api/authApi';
import { queryKeys } from '../query/queryKeys';

/**
 * TanStack Query hooks for every RMC2 module (Rule 56 — pages never hand-roll
 * fetch/cache logic in useEffect).
 *
 * Two conventions run through this file.
 *
 * Unwrapping: the API envelope is `{success, message, data, debug}`, so every
 * queryFn returns `res.data.data` and components deal in payloads, not envelopes.
 *
 * Invalidation: money mutations invalidate WIDELY. Verifying a payment changes
 * the bill, the member, the commission ledger AND the dashboard totals — an
 * admin who sees a stale figure after acting will not trust the next one either.
 */

const EMPTY_PAGE = { items: [], pagination: { page: 1, per_page: 20, total: 0, total_pages: 0 } };

/** Unwrap the standard envelope. */
const payload = (res, fallback = null) => res?.data?.data ?? fallback;

// ── Members ─────────────────────────────────────────────────────────────────

/**
 * The server's live password rules, for the requirements checklist.
 *
 * Cached hard and never refetched on focus: this changes only when an operator
 * edits an env var and restarts, so re-asking on every window focus would be
 * pure noise. Public endpoint — works on the unauthenticated reset page too.
 */
export function usePasswordPolicy() {
  return useQuery({
    queryKey: queryKeys.auth.passwordPolicy(),
    queryFn: async () => payload(await passwordPolicyRequest()),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useMembers(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.members.list(params),
    queryFn: async () => payload(await listMembers(params), EMPTY_PAGE),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Registers a walk-in member from the admin desk.
 *
 * Invalidates the dashboard alongside the roster: a new member immediately
 * changes the activation queue and the registration trend, and an admin who
 * sees a stale count right after acting stops trusting the next one.
 */
export function useCreateMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createMember,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * The signed-in member's own record — the anchor for the whole member portal.
 * Staff have no member row, so a 404 here is a normal answer, not an error worth
 * retrying.
 */
export function useUpdateMyBeneficiaries() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (beneficiaries) => updateMyBeneficiaries(beneficiaries),
    onSuccess: () => {
      // The admin roster shows a beneficiary count, so both views go stale
      // together rather than one of them quietly disagreeing.
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
    },
  });
}

export function useMyMember(enabled = true) {
  return useQuery({
    queryKey: queryKeys.members.me(),
    queryFn: async () => payload(await getMyMember(), null),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Mints a new referral link.
 *
 * NOT cached and never re-fetched: the plain token exists only in this one
 * response, so the component holds it in local state and the user copies it
 * before closing. Rotating invalidates the previous link.
 */
export function useRotateReferralToken() {
  return useMutation({
    mutationFn: async ({ memberId, ...body }) => payload(await rotateReferralToken(memberId, body), null),
  });
}

export function useRevokeReferralToken() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId }) => revokeReferralToken(memberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.members.all }),
  });
}

/**
 * One member in full, including their beneficiaries.
 *
 * The roster row deliberately omits beneficiaries, so any screen that edits a
 * member has to load the record first — submitting a form built from a roster
 * row would send an empty list and wipe them.
 */
export function useMember(memberId) {
  return useQuery({
    queryKey: queryKeys.members.detail(memberId),
    queryFn: async () => payload(await getMember(memberId), null),
    enabled: Boolean(memberId),
    staleTime: 15_000,
  });
}

/**
 * Edits a member's profile.
 *
 * Invalidates the dashboard as well as the roster: a branch move changes which
 * branch's queues the member's open bills appear in, and a stale count right
 * after acting is how an admin stops trusting the numbers.
 */
export function useUpdateMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, ...body }) => payload(await updateMember(memberId, body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Issues or resets a member's portal login.
 *
 * The temporary password is in the mutation result and nowhere else, so the
 * caller must show it immediately — re-reading it later is impossible by design.
 */
export function useResetMemberPassword() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId }) => payload(await resetMemberPassword(memberId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.members.all }),
  });
}

/**
 * Deletes a member with no downline.
 *
 * Invalidates far more than the roster: the cascade soft-deletes the member's
 * bills AND voids the commissions they generated, so the billing ledger, the
 * dashboard totals, the network ledgers and the RAM / RMC2 override balances
 * all just changed.
 */
export function useDeleteMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId }) => payload(await deleteMember(memberId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
      qc.invalidateQueries({ queryKey: queryKeys.overrides.all });
    },
  });
}

/** Queues a roster CSV; pair with `useMemberExport(id)` to poll it. */
export function useCreateMemberExport() {
  return useMutation({
    mutationFn: async (filters) => payload(await createMemberExport(filters)),
  });
}

/**
 * Polls one export until it finishes.
 *
 * Stops polling the moment the row goes terminal — a completed export that kept
 * pinging every two seconds would outlive the page the admin has moved on from.
 */
export function useMemberExport(exportId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.members.export(exportId),
    queryFn: async () => payload(await getMemberExport(exportId)),
    enabled: enabled && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });
}

export function useTransferUpline() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, ...body }) => transferUpline(memberId, body),
    onSuccess: () => {
      // The upline change re-prices future commissions, so the network views
      // are stale too — not just the member row.
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
    },
  });
}

// ── Branches ────────────────────────────────────────────────────────────────

export function useBranches(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.branches.list(params),
    queryFn: async () => payload(await listBranches(params), EMPTY_PAGE),
    // Branches change rarely and are read on nearly every screen (the branch
    // selector), so this is the one list worth holding for a while.
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createBranch,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.branches.all }),
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ branchId, ...body }) => updateBranch(branchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.branches.all }),
  });
}

/** Queues a branch-directory CSV; pair with `useBranchExport(id)` to poll it. */
export function useCreateBranchExport() {
  return useMutation({
    mutationFn: async (filters) => payload(await createBranchExport(filters)),
  });
}

/** Polls one branch export, stopping the moment the row goes terminal. */
export function useBranchExport(exportId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.branches.export(exportId),
    queryFn: async () => payload(await getBranchExport(exportId)),
    enabled: enabled && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });
}

export function useDeleteBranch() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.branches.all }),
  });
}

// ── Billing ─────────────────────────────────────────────────────────────────

export function useBilling(params = {}) {
  return useQuery({
    queryKey: queryKeys.billing.list(params),
    queryFn: async () => payload(await listBilling(params), EMPTY_PAGE),
    staleTime: 15_000,
  });
}

export function useSubmitProof() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ billId, ...fields }) => submitProof(billId, fields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Verify or return a proof of payment.
 *
 * A verification activates the member and pays up to five uplines in one
 * transaction, so this invalidates almost everything: the ledger, the member
 * list, the network tree and the dashboard tiles all just changed.
 */
/**
 * Issues a statement by hand.
 *
 * Invalidates the dashboard too: a new unpaid bill immediately changes the
 * activation queue and the unpaid total an admin is looking at.
 */
export function useIssueBill() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body) => payload(await issueBill(body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Settles a statement paid at the counter.
 *
 * This activates the member and pays their uplines, so it invalidates as widely
 * as a verification does — anything less and an admin sees stale figures right
 * after the most consequential action on the screen.
 */
export function useRecordCounterPayment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ billId, ...body }) => payload(await recordCounterPayment(billId, body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Fetches the short-lived proof link, only while the receipt is open.
 *
 * `staleTime: 0` and no caching beyond the modal: the URL is a bearer credential
 * that expires in minutes, and a cached one would just fail silently later.
 */
export function useBillProof(billId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.billing.proof(billId),
    queryFn: async () => payload(await getBillProof(billId), null),
    enabled: enabled && Boolean(billId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Queues a ledger CSV; pair with `useBillingExport(id)` to poll it. */
export function useCreateBillingExport() {
  return useMutation({
    mutationFn: async (filters) => payload(await createBillingExport(filters)),
  });
}

export function useBillingExport(exportId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.billing.export(exportId),
    queryFn: async () => payload(await getBillingExport(exportId)),
    enabled: enabled && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });
}

export function useDecideBill() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ billId, ...body }) => decideBill(billId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.billing.all });
      qc.invalidateQueries({ queryKey: queryKeys.members.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
      qc.invalidateQueries({ queryKey: queryKeys.reports.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

// ── Network & commissions ───────────────────────────────────────────────────

export function useMyNetwork() {
  return useQuery({
    queryKey: queryKeys.network.myTree(),
    queryFn: async () => payload(await getMyTree(), { nodes: [], counts: {} }),
    staleTime: 60_000,
  });
}

export function useMemberNetwork(memberId) {
  return useQuery({
    queryKey: queryKeys.network.tree(memberId),
    queryFn: async () => payload(await getMemberTree(memberId), { nodes: [], counts: {} }),
    enabled: Boolean(memberId),
    staleTime: 60_000,
  });
}

export function useCommissionSummary(params = {}) {
  return useQuery({
    queryKey: queryKeys.network.summary(params),
    queryFn: async () => payload(await getCommissionSummary(params), null),
    staleTime: 30_000,
  });
}

export function useCommissionLedger(params = {}) {
  return useQuery({
    queryKey: queryKeys.network.ledger(params),
    queryFn: async () => payload(await listCommissions(params), EMPTY_PAGE),
    staleTime: 30_000,
  });
}

// ── Payouts ─────────────────────────────────────────────────────────────────

/**
 * What the member would receive right now, including the promised credit date.
 *
 * `staleTime: 0` is deliberate: this is read at the moment of deciding to
 * withdraw, and a cached balance from ten minutes ago is the wrong number to
 * show someone about to commit.
 */
export function usePayoutPreview(enabled = true) {
  return useQuery({
    queryKey: queryKeys.payouts.preview(),
    queryFn: async () => payload(await previewPayout(), null),
    enabled,
    staleTime: 0,
  });
}

export function usePayouts(params = {}) {
  return useQuery({
    queryKey: queryKeys.payouts.list(params),
    queryFn: async () => payload(await listPayouts(params), EMPTY_PAGE),
    staleTime: 15_000,
  });
}

export function useRequestPayout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: requestPayout,
    onSuccess: () => {
      // The commissions just moved Available → Requested, so the ledger and the
      // preview are both wrong until refetched.
      qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useDecidePayout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutId, ...body }) => decidePayout(payoutId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
      qc.invalidateQueries({ queryKey: queryKeys.network.all });
      qc.invalidateQueries({ queryKey: queryKeys.reports.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

// ── Claims ──────────────────────────────────────────────────────────────────

/**
 * The six benefits and their ceilings.
 *
 * Fetched rather than hardcoded: the catalog is admin-editable rows, so a
 * ceiling raised in Settings must show up here without a deploy.
 */
/**
 * Bank holidays (Rule 74).
 *
 * These rows are what `PayoutSchedulingService` reads to decide a payout's
 * promised credit date, so the whole point of the screen is "is next year's
 * calendar loaded yet?" — the list is queried a year at a time.
 */
export function useBankHolidays(params = {}) {
  return useQuery({
    queryKey: queryKeys.bankHolidays.list(params),
    queryFn: async () => payload(await listBankHolidays(params), EMPTY_PAGE),
    staleTime: 60_000,
  });
}

/**
 * Writes to the holiday calendar.
 *
 * Invalidates payouts alongside the calendar: an admin who has just added a
 * missing holiday wants to see which upcoming credit dates it affects, and a
 * stale list would suggest the edit did nothing. Already-filed requests keep
 * their stamped date by design (Rule 74) — this refreshes the view, not the
 * promises.
 */
export function useSaveBankHoliday() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ holidayId, ...body }) => (
      holidayId
        ? payload(await updateBankHoliday(holidayId, body))
        : payload(await createBankHoliday(body))
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bankHolidays.all });
      qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
    },
  });
}

export function useDeleteBankHoliday() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (holidayId) => payload(await deleteBankHoliday(holidayId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bankHolidays.all });
      qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
    },
  });
}

/**
 * Adjusts a benefit ceiling.
 *
 * Invalidates claims too: the ceiling bounds what an admin may approve, so a
 * decision screen still holding the old number would offer an amount the server
 * is about to refuse.
 */
export function useUpdateBenefit() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ benefitKey, ...body }) => payload(await updateBenefit(benefitKey, body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.claims.all });
    },
  });
}

export function useBenefitCatalog() {
  return useQuery({
    queryKey: queryKeys.claims.catalog(),
    queryFn: async () => payload(await getBenefitCatalog(), { items: [] }),
    staleTime: 10 * 60_000,
  });
}

export function useClaims(params = {}) {
  return useQuery({
    queryKey: queryKeys.claims.list(params),
    queryFn: async () => payload(await listClaims(params), EMPTY_PAGE),
    staleTime: 15_000,
  });
}

export function useFileClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: fileClaim,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.claims.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useAppealClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, ...body }) => appealClaim(claimId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.claims.all }),
  });
}

export function useDecideClaim() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, ...body }) => decideClaim(claimId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.claims.all });
      qc.invalidateQueries({ queryKey: queryKeys.reports.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * The supporting documents on one claim.
 *
 * Metadata only — filenames, sizes, labels. Opening one is a separate request
 * because the link that opens it expires in minutes.
 */
export function useClaimAttachments(claimId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.claims.attachments(claimId),
    queryFn: async () => payload(await listClaimAttachments(claimId), { items: [] }),
    enabled: enabled && Boolean(claimId),
    staleTime: 15_000,
  });
}

/**
 * Fetches the short-lived link for one document, only while it is open.
 *
 * `staleTime: 0` and no caching: the URL is a bearer credential for a medical
 * record that expires in ten minutes, and a cached one would just fail later.
 */
export function useClaimAttachmentLink(claimId, attachmentId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.claims.attachmentLink(claimId, attachmentId),
    queryFn: async () => payload(await getClaimAttachmentLink(claimId, attachmentId), null),
    enabled: enabled && Boolean(claimId) && Boolean(attachmentId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useAttachClaimDocument() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ claimId, ...body }) => attachClaimDocument(claimId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.claims.attachments(variables.claimId) });
      // The list renders an "N docs" affordance from the claim row, so it has
      // to be refetched too or the count stays stale behind the modal.
      qc.invalidateQueries({ queryKey: queryKeys.claims.listAll });
    },
  });
}

/** Queues a claims CSV; pair with `useClaimsExport(id)` to poll it. */
export function useCreateClaimsExport() {
  return useMutation({
    mutationFn: async (filters) => payload(await createClaimsExport(filters)),
  });
}

export function useClaimsExport(exportId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.claims.export(exportId),
    queryFn: async () => payload(await getClaimsExport(exportId)),
    enabled: enabled && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });
}

export function useReschedulePayout() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutId, ...body }) => reschedulePayout(payoutId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payouts.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/** Queues a report CSV; pair with `useReportExport(id)` to poll it. */
export function useCreateReportExport() {
  return useMutation({
    mutationFn: async (body) => payload(await createReportExport(body)),
  });
}

export function useReportExport(exportId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.reports.export(exportId),
    queryFn: async () => payload(await getReportExport(exportId)),
    enabled: enabled && Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });
}

/**
 * The house override ledger.
 *
 * Carries a `balances` map alongside the page — available / requested /
 * released / lifetime per house account. Those are the figures the release
 * request is filed against, so they come from the server rather than being
 * summed over whatever rows happen to be visible.
 */
export function useOverrideLedger(params = {}) {
  return useQuery({
    queryKey: queryKeys.overrides.ledger(params),
    queryFn: async () => payload(await getOverrideLedger(params), EMPTY_PAGE),
    staleTime: 15_000,
  });
}

export function useOverrideReleases(params = {}) {
  return useQuery({
    queryKey: queryKeys.overrides.releases(params),
    queryFn: async () => payload(await listOverrideReleases(params), EMPTY_PAGE),
    staleTime: 15_000,
  });
}

export function useRequestOverrideRelease() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body) => payload(await requestOverrideRelease(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.overrides.all }),
  });
}

export function useApproveOverrideRelease() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ overrideId, ...body }) => approveOverrideRelease(overrideId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.overrides.all });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

// ── Helpdesk ────────────────────────────────────────────────────────────────

export function useTickets(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.support.list(params),
    queryFn: async () => payload(await listTickets(params), EMPTY_PAGE),
    staleTime: 15_000,
    ...options,
  });
}

/** How many messages a page of the conversation carries. Mirrors the server default. */
export const TICKET_PAGE_SIZE = 30;

/**
 * One ticket's conversation, paged backwards from the newest message.
 *
 * An infinite query rather than a plain one because a thread is append-only and
 * unbounded: page 0 is the LATEST batch, and each `fetchNextPage()` walks
 * further into the past. So the render order is the page list REVERSED and then
 * flattened — `useTicketMessages` below does that so no caller has to remember.
 */
export function useTicketThread(ticketId) {
  return useInfiniteQuery({
    queryKey: queryKeys.support.thread(ticketId),
    queryFn: async ({ pageParam }) => payload(
      await getTicketThread(ticketId, { beforeId: pageParam, limit: TICKET_PAGE_SIZE }),
      { ticket: null, items: [], cursor: { has_more: false, next_before_id: null } },
    ),
    initialPageParam: undefined,
    getNextPageParam: (last) => last?.cursor?.next_before_id ?? undefined,
    enabled: Boolean(ticketId),
    staleTime: 10_000,
  });
}

/**
 * Flattens an infinite thread into the chronological list the UI renders.
 *
 * Only page 0 carries the ticket header — the server omits it on older pages so
 * the member's decrypted contact detail is not reissued on every scroll-up.
 */
export function useTicketMessages(query) {
  // Depend on `query.data?.pages` directly — deriving `pages` outside the memo
  // would produce a fresh `[]` on every render and defeat it entirely.
  const pages = query.data?.pages;

  return useMemo(() => {
    const loaded = pages ?? [];

    return {
      ticket: loaded[0]?.ticket ?? null,
      messages: [...loaded].reverse().flatMap((p) => p?.items ?? []),
    };
  }, [pages]);
}

/** Moves the caller's read watermark and refreshes the badges that depend on it. */
export function useMarkTicketRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId }) => markTicketRead(ticketId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.support.unread() });
      qc.invalidateQueries({ queryKey: queryKeys.support.listAll });
    },
  });
}

/**
 * The nav badge count.
 *
 * Refetched on window focus rather than polled: the realtime layer already
 * pushes the events that change it, and a timer would spend requests on the
 * overwhelming majority of minutes where nothing happened.
 */
export function useSupportUnread(enabled = true) {
  return useQuery({
    queryKey: queryKeys.support.unread(),
    queryFn: async () => payload(await getSupportUnread(), { unread_tickets: 0, unread_messages: 0 }),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createTicket,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.support.all }),
  });
}

/**
 * Posts a reply, and paints it in the thread before the server answers.
 *
 * The optimistic row exists because this screen is a conversation: a message
 * that only appears after a round trip reads as "did that send?", and the
 * retry that follows is how you get duplicates. It carries `pending: true` so
 * the bubble can show as in-flight, and a negative id so it can never collide
 * with a real `support_messages.id`.
 *
 * `onSettled` re-fetches either way, which is also what reconciles the
 * optimistic row with the server's version (real id, real timestamp) — the
 * broadcast the sender receives from their own reply invalidates the same key,
 * so the two paths converge on one refetch rather than fighting.
 */
export function useReplyToTicket() {
  const qc = useQueryClient();

  return useMutation({
    // `senderName` / `asAgent` are for the optimistic bubble only — stripped
    // here so they never reach a request body that has no field for them.
    mutationFn: ({ ticketId, senderName, asAgent, ...body }) => replyToTicket(ticketId, body),

    onMutate: async ({ ticketId, text, isInternal, attachment, senderName, asAgent }) => {
      const key = queryKeys.support.thread(ticketId);

      // Stop an in-flight refetch from landing on top of the optimistic row.
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData(key);

      // Page 0 of the infinite thread is the NEWEST batch, so that is where a
      // just-sent message belongs. Patching a flat `items` here would silently
      // no-op now that the thread pages.
      qc.setQueryData(key, (old) => {
        if (!old?.pages?.length) return old;

        const [newest, ...rest] = old.pages;

        return {
          ...old,
          pages: [{
            ...newest,
            items: [...(newest.items ?? []), {
              id: -Date.now(),
              sender: asAgent ? 'admin' : 'member',
              sender_name: senderName ?? null,
              text,
              is_internal: Boolean(isInternal),
              created_at: new Date().toISOString(),
              seen: false,
              // Named from the File so the bubble can show what is going up,
              // without reading the bytes.
              attachment: attachment ? { name: attachment.name, mime: attachment.type, size: attachment.size } : null,
              pending: true,
            }],
          }, ...rest],
        };
      });

      return { previous, key };
    },

    onError: (_err, _vars, context) => {
      // Put the thread back exactly as it was — a failed send must not leave a
      // message on screen that the server never received.
      if (context?.previous !== undefined) qc.setQueryData(context.key, context.previous);
    },

    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.support.thread(vars.ticketId) });
      qc.invalidateQueries({ queryKey: queryKeys.support.listAll });
    },
  });
}

export function useSetTicketStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, status }) => setTicketStatus(ticketId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.support.all }),
  });
}

export function useAssignTicket() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, assignedTo }) => assignTicket(ticketId, assignedTo),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.support.all });
      qc.invalidateQueries({ queryKey: queryKeys.support.thread(variables.ticketId) });
    },
  });
}

// ── Dashboard ───────────────────────────────────────────────────────────────

/**
 * The Admin Operations Dashboard, in one request.
 *
 * `staleTime` is short because the whole point of this screen is "what needs me
 * right now" — an operator who verifies a payment and comes back to a stale
 * queue count stops trusting the number. Every money mutation below also
 * invalidates this key explicitly.
 */
export function useOperationsDashboard(options = {}) {
  return useQuery({
    queryKey: queryKeys.dashboard.operations(),
    queryFn: async () => payload(await getOperationsDashboard(), null),
    staleTime: 30_000,
    ...options,
  });
}

// ── Reports ─────────────────────────────────────────────────────────────────

export function useReportSummary(params = {}) {
  return useQuery({
    queryKey: queryKeys.reports.summary(params),
    queryFn: async () => payload(await getReportSummary(params), null),
    staleTime: 60_000,
  });
}

export function useAcquisitionReport(params = {}) {
  return useQuery({
    queryKey: queryKeys.reports.acquisition(params),
    queryFn: async () => payload(await getAcquisition(params), { items: [] }),
    staleTime: 5 * 60_000,
  });
}

export function useBranchPerformance(options = {}) {
  return useQuery({
    queryKey: queryKeys.reports.branchPerformance(),
    queryFn: async () => payload(await getBranchPerformance(), { items: [] }),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useRevenueTrend(params = {}) {
  return useQuery({
    queryKey: queryKeys.reports.revenue(params),
    queryFn: async () => payload(await getRevenueTrend(params), { items: [] }),
    staleTime: 5 * 60_000,
  });
}
